/**
 * OOS Filters Component
 *
 * Filter bar for OOS issues tab with search, status, source, age, and sort filters.
 * Uses h-12 inputs for warehouse UX (glove-friendly touch targets).
 */
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  OosFilterState,
  OosStatusFilter,
  OosSourceFilter,
  OosAgeFilter,
  OosSortOption,
  OosContactedFilter,
} from '../types';
import { DEFAULT_OOS_FILTERS } from '../types';

interface OosFiltersProps {
  filters: OosFilterState;
  onFilterChange: <K extends keyof OosFilterState>(
    key: K,
    value: OosFilterState[K]
  ) => void;
  onReset: () => void;
  resultCount: number;
  /**
   * When true, the result-count chip switches to "{N} matching" so the user
   * can tell the count reflects the active filters — not the global tab
   * count shown in the page header. Optional; falls back to a local derive
   * so older callers keep working.
   */
  hasActiveFilters?: boolean;
}

export function OosFilters({
  filters,
  onFilterChange,
  onReset,
  resultCount,
  hasActiveFilters: hasActiveFiltersProp,
}: OosFiltersProps) {
  const { t } = useTranslation('production');

  // Check if any filter is active (not at default value)
  const localHasActiveFilters =
    filters.status !== DEFAULT_OOS_FILTERS.status ||
    filters.source !== DEFAULT_OOS_FILTERS.source ||
    filters.ageFilter !== DEFAULT_OOS_FILTERS.ageFilter ||
    filters.skuSearch !== DEFAULT_OOS_FILTERS.skuSearch ||
    filters.search !== DEFAULT_OOS_FILTERS.search ||
    filters.contactedFilter !== DEFAULT_OOS_FILTERS.contactedFilter;
  const hasActiveFilters = hasActiveFiltersProp ?? localHasActiveFilters;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      {/* Search input - flex-1 to take available space */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('issues.filters.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          className="pl-10 pr-10 h-12"
          maxLength={100}
          aria-label={t('issues.filters.searchAriaLabel')}
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => onFilterChange('search', '')}
            aria-label={t('issues.filters.clearSearch')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Status filter */}
      <Select
        value={filters.status}
        onValueChange={(v) => onFilterChange('status', v as OosStatusFilter)}
      >
        <SelectTrigger className="w-[160px] h-12" aria-label={t('issues.filters.statusAriaLabel')}>
          <SelectValue placeholder={t('issues.filters.status')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('issues.filters.allStatus')}</SelectItem>
          <SelectItem value="needs_decision">{t('issues.filters.needsDecision')}</SelectItem>
          <SelectItem value="awaiting_inventory">{t('issues.filters.awaitingInventory')}</SelectItem>
          <SelectItem value="routed">{t('issues.filters.routed')}</SelectItem>
        </SelectContent>
      </Select>

      {/* Source filter - Only Pre-batch and Picking (no Packing) */}
      <Select
        value={filters.source}
        onValueChange={(v) => onFilterChange('source', v as OosSourceFilter)}
      >
        <SelectTrigger className="w-[140px] h-12" aria-label={t('issues.filters.sourceAriaLabel')}>
          <SelectValue placeholder={t('issues.filters.source')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('issues.filters.allSources')}</SelectItem>
          <SelectItem value="pre_batch">{t('issues.filters.preBatch')}</SelectItem>
          <SelectItem value="picking">{t('issues.filters.picking')}</SelectItem>
        </SelectContent>
      </Select>

      {/* Contacted filter */}
      <Select
        value={filters.contactedFilter}
        onValueChange={(v) => onFilterChange('contactedFilter', v as OosContactedFilter)}
      >
        <SelectTrigger className="w-[160px] h-12" aria-label={t('issues.filters.contactedAriaLabel')}>
          <SelectValue placeholder={t('issues.filters.contacted')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('issues.filters.allContacted')}</SelectItem>
          <SelectItem value="contacted">{t('issues.filters.contactedOnly')}</SelectItem>
          <SelectItem value="not_contacted">{t('issues.filters.notContactedOnly')}</SelectItem>
        </SelectContent>
      </Select>

      {/* Age filter */}
      <Select
        value={filters.ageFilter}
        onValueChange={(v) => onFilterChange('ageFilter', v as OosAgeFilter)}
      >
        <SelectTrigger className="w-[130px] h-12" aria-label={t('issues.filters.ageAriaLabel')}>
          <SelectValue placeholder={t('issues.filters.age')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('issues.filters.allTime')}</SelectItem>
          <SelectItem value="today">{t('issues.filters.today')}</SelectItem>
          <SelectItem value="this_week">{t('issues.filters.thisWeek')}</SelectItem>
          <SelectItem value="older">{t('issues.filters.older')}</SelectItem>
        </SelectContent>
      </Select>

      {/* Sort */}
      <Select
        value={filters.sort}
        onValueChange={(v) => onFilterChange('sort', v as OosSortOption)}
      >
        <SelectTrigger className="w-[150px] h-12" aria-label={t('issues.filters.sortAriaLabel')}>
          <SelectValue placeholder={t('issues.filters.sort')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="oldest">{t('issues.filters.oldestFirst')}</SelectItem>
          <SelectItem value="newest">{t('issues.filters.newestFirst')}</SelectItem>
          <SelectItem value="order_number">{t('issues.filters.orderNumber')}</SelectItem>
        </SelectContent>
      </Select>

      {/* Reset button + Result count */}
      <div className="flex items-center gap-2">
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onReset} className="h-10">
            <X className="h-4 w-4 mr-1" />
            {t('issues.filters.reset')}
          </Button>
        )}
        <span className="text-sm text-muted-foreground">
          {hasActiveFilters
            ? t('issues.filters.matchingCount', { count: resultCount })
            : t('issues.filters.orderCount', { count: resultCount })}
        </span>
      </div>
    </div>
  );
}
